import { useMemo } from 'react';
import type {
  MarketplaceCcgReviewRequest,
  GetEbayCategoryPolicyResponse,
  ImportedListingPreviewRequest,
  ImportedListingPreviewResponse,
  ImportedListingApplyRequest,
  ImportedListingApplyResponse,
  GenerateMarketplaceListingDescriptionRequest,
  GenerateMarketplaceListingDescriptionResponse,
  ListMarketplaceListingReadinessResponse,
  MarketplaceCardComparablesResponse,
  EbayActiveComparablesResponse,
  ListMarketplaceListingTemplatesResponse,
  MarketplaceChecklistTemplateAssignmentResponse,
  MarketplaceListingCopyResponse,
  MarketplaceListingReadinessResponse,
  MarketplaceListingIdentitySource,
  MarketplaceListingTemplateResponse,
  MarketplaceListingReviewContextResponse,
  SaveMarketplaceListingReadinessRequest,
  SaveMarketplaceChecklistTemplateAssignmentRequest,
  SaveMarketplaceListingTemplateRequest,
  SetMarketplaceDefaultListingTemplateResponse,
  EbaySellerHubExportPreviewResponse,
  EbaySellerHubExportDownloadResponse,
  EbaySellerHubExportRequest,
  ListMarketplaceListingActivitiesResponse,
  MarketplaceListingActivityReturnReason,
  ReturnMarketplaceListingActivityItemsResponse,
  EbaySellerHubResultResponse,
  EbayPublishCardRequest,
  EbayPublishPreviewResponse,
  EbayPublishResponse,
  CardIdentity,
  CardPresentation,
  EbayItemSpecifics,
  EbaySellerHubSinglesCategory,
  ChecklistSourceSelection,
  ConfirmFulfillmentPickRequest,
  ConfirmSelectedFulfillmentPicksRequest,
  FulfillmentPicklistActionRequest,
  FulfillmentPicklistDetailResponse,
  GenerateFulfillmentPicklistRequest,
  ListFulfillmentPicklistsResponse,
  ListFulfillmentTasksResponse,
} from '@tcvs/shared';

export interface MarketplaceListingEditLock {
  state: 'LISTED' | 'SOLD';
  marketplace: 'EBAY_US';
  marketplaceListingId: string;
  externalListingId?: string;
}

type MarketplaceListingCopyWithEditLock = MarketplaceListingCopyResponse & {
  editLock?: MarketplaceListingEditLock;
};
import type {
  EbayConnectionHealthResponse,
  EbayOAuthStartResponse,
  EbayReconciliationResponse,
  EbayReconciliationView,
  ListEbayObservedListingsResponse,
  ListEbayObservedOrdersResponse,
  ListEbaySyncExceptionsResponse,
} from '@tcvs/shared/dist/ebay-live';
import { useApi } from '../api';
import { parseCcgPolicyResponse } from './ccg-review-state';
import type { ApiResult } from '../auth/api-client';

export function useMarketplaceApi() {
  const api = useApi();
  return useMemo(
    () => ({
      getEbayCcgCategoryPolicy: async (options?: {
        signal?: AbortSignal;
      }): Promise<ApiResult<GetEbayCategoryPolicyResponse>> => {
        const result = await api.request<unknown>('/marketplace/listing-templates', {
          method: 'POST',
          body: { operation: 'GET_EBAY_CATEGORY_POLICY', category: 'CCG_SINGLES' },
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
        });
        if (!result.ok) return result;
        try {
          return { ...result, data: parseCcgPolicyResponse(result.data) };
        } catch {
          return {
            ok: false,
            status: 502,
            error: 'ccg_policy_unavailable',
            message: 'Current CCG policy is unavailable. Reload and review again.',
          };
        }
      },
      listFulfillmentTasks: (): Promise<ApiResult<ListFulfillmentTasksResponse>> =>
        api.request<ListFulfillmentTasksResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_LIST_TASKS' },
        }),
      reconcileFulfillmentTasks: (): Promise<ApiResult<ListFulfillmentTasksResponse>> =>
        api.request<ListFulfillmentTasksResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_RECONCILE_TASKS' },
        }),
      listFulfillmentPicklists: (): Promise<ApiResult<ListFulfillmentPicklistsResponse>> =>
        api.request<ListFulfillmentPicklistsResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_LIST_PICKLISTS' },
        }),
      generateFulfillmentPicklist: (
        body: GenerateFulfillmentPicklistRequest,
      ): Promise<ApiResult<FulfillmentPicklistDetailResponse>> =>
        api.request<FulfillmentPicklistDetailResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_GENERATE_PICKLIST', ...body },
        }),
      getFulfillmentPicklist: (
        picklistId: string,
      ): Promise<ApiResult<FulfillmentPicklistDetailResponse>> =>
        api.request<FulfillmentPicklistDetailResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_GET_PICKLIST', picklistId },
        }),
      mutateFulfillmentPicklist: (
        picklistId: string,
        body: FulfillmentPicklistActionRequest,
      ): Promise<ApiResult<FulfillmentPicklistDetailResponse>> =>
        api.request<FulfillmentPicklistDetailResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_MUTATE_PICKLIST', picklistId, ...body },
        }),
      confirmFulfillmentPick: (
        picklistId: string,
        body: ConfirmFulfillmentPickRequest,
      ): Promise<ApiResult<FulfillmentPicklistDetailResponse>> =>
        api.request<FulfillmentPicklistDetailResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_CONFIRM_PICK', picklistId, ...body },
        }),
      confirmSelectedFulfillmentPicks: (
        picklistId: string,
        body: ConfirmSelectedFulfillmentPicksRequest,
      ): Promise<ApiResult<FulfillmentPicklistDetailResponse>> =>
        api.request<FulfillmentPicklistDetailResponse>('/marketplace', {
          method: 'POST',
          body: { operation: 'FULFILLMENT_CONFIRM_SELECTED', picklistId, ...body },
        }),
      previewImportedListing: (
        body: Omit<ImportedListingPreviewRequest, 'operation'>,
      ): Promise<ApiResult<ImportedListingPreviewResponse>> =>
        api.request<ImportedListingPreviewResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { ...body, operation: 'PREVIEW_IMPORTED_LISTING' },
        }),
      applyImportedListing: (
        body: Omit<ImportedListingApplyRequest, 'operation'>,
      ): Promise<ApiResult<ImportedListingApplyResponse>> =>
        api.request<ImportedListingApplyResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { ...body, operation: 'APPLY_IMPORTED_LISTING' },
        }),
      listListingTemplates: (): Promise<ApiResult<ListMarketplaceListingTemplatesResponse>> =>
        api.request<ListMarketplaceListingTemplatesResponse>('/marketplace/listing-templates'),
      createListingTemplate: (
        body: SaveMarketplaceListingTemplateRequest,
      ): Promise<ApiResult<MarketplaceListingTemplateResponse>> =>
        api.request<MarketplaceListingTemplateResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body,
        }),
      updateListingTemplate: (
        templateId: string,
        body: SaveMarketplaceListingTemplateRequest,
      ): Promise<ApiResult<MarketplaceListingTemplateResponse>> =>
        api.request<MarketplaceListingTemplateResponse>(
          `/marketplace/listing-templates/${encodeURIComponent(templateId)}`,
          { method: 'PUT', body },
        ),
      setDefaultListingTemplate: (
        templateId: string | null,
        scope?: 'CCG',
      ): Promise<ApiResult<SetMarketplaceDefaultListingTemplateResponse>> =>
        api.request<SetMarketplaceDefaultListingTemplateResponse>(
          '/marketplace/listing-templates',
          {
            method: 'POST',
            body: {
              operation: 'SET_DEFAULT_LISTING_TEMPLATE',
              templateId,
              ...(scope === undefined ? {} : { scope }),
            },
          },
        ),
      getChecklistTemplateAssignment: (
        checklistId: string,
      ): Promise<ApiResult<MarketplaceChecklistTemplateAssignmentResponse>> =>
        api.request<MarketplaceChecklistTemplateAssignmentResponse>(
          `/marketplace/checklist-template-assignments/${encodeURIComponent(checklistId)}`,
        ),
      updateChecklistTemplateAssignment: (
        checklistId: string,
        body: SaveMarketplaceChecklistTemplateAssignmentRequest,
      ): Promise<ApiResult<MarketplaceChecklistTemplateAssignmentResponse>> =>
        api.request<MarketplaceChecklistTemplateAssignmentResponse>(
          `/marketplace/checklist-template-assignments/${encodeURIComponent(checklistId)}`,
          { method: 'PUT', body },
        ),
      getListingReviewContext: (
        checklistId: string,
      ): Promise<ApiResult<MarketplaceListingReviewContextResponse>> =>
        api
          .request<MarketplaceChecklistTemplateAssignmentResponse>(
            `/marketplace/checklist-template-assignments/${encodeURIComponent(checklistId)}`,
          )
          .then((result) =>
            result.ok
              ? {
                  ...result,
                  data: {
                    checklistId,
                    assignedTemplateCount: result.data.assignment.templateIds.length,
                    templates: result.data.templates ?? [],
                    templateSource: result.data.templateSource ?? 'NONE',
                    ...(result.data.defaultTemplateId === undefined
                      ? {}
                      : { defaultTemplateId: result.data.defaultTemplateId }),
                  },
                }
              : result,
          ),
      generateListingDescription: (
        body: GenerateMarketplaceListingDescriptionRequest,
      ): Promise<ApiResult<GenerateMarketplaceListingDescriptionResponse>> =>
        api.request<GenerateMarketplaceListingDescriptionResponse>(
          '/marketplace/listing-templates',
          { method: 'POST', body },
        ),
      loadListingCopy: (cardId: string): Promise<ApiResult<MarketplaceListingCopyWithEditLock>> =>
        api.request<MarketplaceListingCopyWithEditLock>('/marketplace/listing-templates', {
          method: 'POST',
          body: { operation: 'LOAD_LISTING_COPY', cardId },
        }),
      saveListingCopy: (
        cardId: string,
        body: {
          title: string;
          description?: string;
          templateId: string;
          identitySource?: MarketplaceListingIdentitySource;
          checklistId?: string;
          identity: CardIdentity;
          cardPresentation?: CardPresentation;
          itemSpecifics?: EbayItemSpecifics;
          itemSpecificsCategory?: EbaySellerHubSinglesCategory;
          ccgReview?: MarketplaceCcgReviewRequest;
        },
      ): Promise<ApiResult<MarketplaceListingCopyWithEditLock>> =>
        api.request<MarketplaceListingCopyWithEditLock>('/marketplace/listing-templates', {
          method: 'POST',
          body:
            body.itemSpecificsCategory === 'CCG_SINGLES'
              ? {
                  operation: 'SAVE_LISTING_COPY',
                  cardId,
                  ...ccgSaveBody(body),
                  identity: ccgIdentity(body.identity),
                }
              : { operation: 'SAVE_LISTING_COPY', cardId, ...body },
        }),
      getCardComparables: (
        cardId: string,
        identity: CardIdentity,
        cardPresentation?: CardPresentation,
        checklistSourceSelection?: ChecklistSourceSelection,
        currentBatchId?: string,
      ): Promise<ApiResult<MarketplaceCardComparablesResponse>> =>
        api.request<MarketplaceCardComparablesResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: {
            operation: 'CARD_COMPARABLES',
            cardId,
            identity,
            ...(cardPresentation === undefined ? {} : { cardPresentation }),
            ...(checklistSourceSelection === undefined ? {} : { checklistSourceSelection }),
            ...(currentBatchId === undefined ? {} : { currentBatchId }),
          },
        }),
      getLiveEbayComparables: (
        cardId: string,
        identity: CardIdentity,
        cardPresentation: CardPresentation | undefined,
        checklistSourceSelection: ChecklistSourceSelection,
        itemSpecificsCategory: EbaySellerHubSinglesCategory,
        forceRefresh = false,
      ): Promise<ApiResult<EbayActiveComparablesResponse>> =>
        api.request<EbayActiveComparablesResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: {
            operation: 'LIVE_EBAY_COMPARABLES',
            cardId,
            identity,
            ...(cardPresentation === undefined ? {} : { cardPresentation }),
            checklistSourceSelection,
            itemSpecificsCategory,
            ...(forceRefresh ? { forceRefresh: true } : {}),
          },
        }),
      saveListingReadiness: (
        cardId: string,
        body: SaveMarketplaceListingReadinessRequest,
      ): Promise<ApiResult<MarketplaceListingReadinessResponse>> =>
        api.request<MarketplaceListingReadinessResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body:
            body.itemSpecificsCategory === 'CCG_SINGLES'
              ? {
                  operation: 'SAVE_LISTING_READINESS',
                  cardId,
                  ...ccgSaveBody(body),
                  amountMinor: body.amountMinor,
                  currency: body.currency,
                }
              : { operation: 'SAVE_LISTING_READINESS', cardId, ...body },
        }),
      listListingReadiness: (
        status: 'READY_TO_LIST' | 'APPROVED_FOR_EBAY',
        cursor?: string,
        includeDuplicateInventory = false,
      ): Promise<ApiResult<ListMarketplaceListingReadinessResponse>> =>
        api.request<ListMarketplaceListingReadinessResponse>('/marketplace/listing-templates', {
          query: {
            readinessStatus: status,
            cursor,
            ...(includeDuplicateInventory ? { includeDuplicateInventory: 'true' } : {}),
          },
        }),
      approveListingReadiness: (
        cardId: string,
      ): Promise<ApiResult<MarketplaceListingReadinessResponse>> =>
        api.request<MarketplaceListingReadinessResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { operation: 'APPROVE_LISTING_READINESS', cardId },
        }),
      previewEbayPublish: (
        cards: EbayPublishCardRequest[],
      ): Promise<ApiResult<EbayPublishPreviewResponse>> =>
        api.request<EbayPublishPreviewResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { operation: 'EBAY_PUBLISH', mode: 'PREVIEW', cards },
        }),
      publishToEbay: (cards: EbayPublishCardRequest[]): Promise<ApiResult<EbayPublishResponse>> =>
        api.request<EbayPublishResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { operation: 'EBAY_PUBLISH', mode: 'PUBLISH', cards },
        }),
      previewSellerHubExport: (
        body: Omit<EbaySellerHubExportRequest, 'operation'>,
      ): Promise<ApiResult<EbaySellerHubExportPreviewResponse>> =>
        api.request<EbaySellerHubExportPreviewResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { ...body, operation: 'SELLER_HUB_PREVIEW' },
        }),
      generateSellerHubExport: (
        body: Omit<EbaySellerHubExportRequest, 'operation'>,
      ): Promise<ApiResult<EbaySellerHubExportPreviewResponse>> =>
        api.request<EbaySellerHubExportPreviewResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { ...body, operation: 'SELLER_HUB_GENERATE' },
        }),
      downloadSellerHubExport: (
        fingerprint: string,
      ): Promise<ApiResult<EbaySellerHubExportDownloadResponse>> =>
        api.request<EbaySellerHubExportDownloadResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: { operation: 'SELLER_HUB_DOWNLOAD', fingerprint },
        }),
      releaseSellerHubExport: (
        fingerprint: string,
      ): Promise<ApiResult<{ fingerprint: string; status: 'ABANDONED' }>> =>
        api.request<{ fingerprint: string; status: 'ABANDONED' }>(
          '/marketplace/listing-templates',
          { method: 'POST', body: { operation: 'SELLER_HUB_RELEASE', fingerprint } },
        ),
      listMarketplaceListingActivities: (
        cursor?: string,
        limit = 10,
      ): Promise<ApiResult<ListMarketplaceListingActivitiesResponse>> =>
        api.request<ListMarketplaceListingActivitiesResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: {
            operation: 'LIST_MARKETPLACE_LISTING_ACTIVITIES',
            limit,
            ...(cursor === undefined ? {} : { cursor }),
          },
        }),
      returnMarketplaceListingActivityItems: (
        fingerprint: string,
        cardIds: string[],
        reason: MarketplaceListingActivityReturnReason,
      ): Promise<ApiResult<ReturnMarketplaceListingActivityItemsResponse>> =>
        api.request<ReturnMarketplaceListingActivityItemsResponse>(
          '/marketplace/listing-templates',
          {
            method: 'POST',
            body: {
              operation: 'RETURN_MARKETPLACE_LISTING_ACTIVITY_ITEMS',
              fingerprint,
              cardIds,
              reason,
            },
          },
        ),
      previewSellerHubResults: (
        fingerprint: string,
        sourceFilename: string,
        csv: string,
      ): Promise<ApiResult<EbaySellerHubResultResponse>> =>
        api.request<EbaySellerHubResultResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: {
            operation: 'SELLER_HUB_RESULTS_PREVIEW',
            fingerprint,
            sourceFilename,
            csv,
          },
        }),
      applySellerHubResults: (
        fingerprint: string,
        sourceFilename: string,
        csv: string,
        resultFingerprint: string,
      ): Promise<ApiResult<EbaySellerHubResultResponse>> =>
        api.request<EbaySellerHubResultResponse>('/marketplace/listing-templates', {
          method: 'POST',
          body: {
            operation: 'SELLER_HUB_RESULTS_APPLY',
            fingerprint,
            sourceFilename,
            csv,
            resultFingerprint,
          },
        }),
      getEbayConnectionHealth: (): Promise<ApiResult<EbayConnectionHealthResponse>> =>
        api.request<EbayConnectionHealthResponse>('/marketplace/ebay/imports', {
          query: { live: 'connection' },
        }),
      startEbayOAuth: (): Promise<ApiResult<EbayOAuthStartResponse>> =>
        api.request<EbayOAuthStartResponse>('/marketplace/ebay/imports', {
          method: 'POST',
          body: { liveAction: 'oauth-start' },
        }),
      disconnectEbay: (): Promise<ApiResult<{ status: 'DISCONNECTED' }>> =>
        api.request<{ status: 'DISCONNECTED' }>('/marketplace/ebay/imports', {
          method: 'POST',
          body: { liveAction: 'disconnect' },
        }),
      requestEbaySync: (
        mode: 'ALL' | 'INVENTORY' | 'ORDERS' = 'ALL',
      ): Promise<ApiResult<{ accepted: true; mode: string }>> =>
        api.request<{ accepted: true; mode: string }>('/marketplace/ebay/imports', {
          method: 'POST',
          body: {
            liveAction: 'sync',
            mode,
            force: mode === 'ALL' || mode === 'INVENTORY',
          },
        }),
      listEbayObservedListings: (
        status: 'ACTIVE' | 'MISSING' | 'ENDED' | 'SOLD' = 'ACTIVE',
        cursor?: string,
      ): Promise<ApiResult<ListEbayObservedListingsResponse>> =>
        api.request<ListEbayObservedListingsResponse>('/marketplace/ebay/imports', {
          query: { live: 'listings', status, cursor, limit: 25 },
        }),
      listEbayObservedOrders: (
        cursor?: string,
      ): Promise<ApiResult<ListEbayObservedOrdersResponse>> =>
        api.request<ListEbayObservedOrdersResponse>('/marketplace/ebay/imports', {
          query: { live: 'orders', cursor, limit: 25 },
        }),
      listEbaySyncExceptions: (
        cursor?: string,
      ): Promise<ApiResult<ListEbaySyncExceptionsResponse>> =>
        api.request<ListEbaySyncExceptionsResponse>('/marketplace/ebay/imports', {
          query: { live: 'exceptions', cursor, limit: 25 },
        }),
      getEbayReconciliation: (
        view: EbayReconciliationView = 'ACTIVE',
        cursor?: string,
      ): Promise<ApiResult<EbayReconciliationResponse>> =>
        api.request<EbayReconciliationResponse>('/marketplace/ebay/imports', {
          query: { live: 'reconciliation', view, cursor, limit: 25 },
        }),
    }),
    [api],
  );
}

/** Literal allowlist: browser/Card/policy provenance extras never become save authority. */
function ccgSaveBody(body: {
  title: string;
  description?: string;
  templateId: string;
  identitySource?: MarketplaceListingIdentitySource;
  checklistId?: string;
  cardPresentation?: CardPresentation;
  itemSpecifics?: EbayItemSpecifics;
  ccgReview?: MarketplaceCcgReviewRequest;
}) {
  const review = body.ccgReview;
  if (!review) throw new Error('Review current CCG details before saving.');
  const specifics: MarketplaceCcgReviewRequest['specifics'] = {};
  for (const name of ['Finish', 'Rarity'] as const) {
    const d = review.specifics[name];
    if (
      d &&
      (!['MANUAL_REVIEW', 'IMPORTED_RESEARCH'].includes(d.origin) || typeof d.value !== 'string')
    )
      throw new Error('Invalid CCG review.');
    if (d) specifics[name] = { origin: d.origin, value: d.value };
  }
  return {
    title: body.title,
    ...(body.description === undefined ? {} : { description: body.description }),
    templateId: body.templateId,
    identitySource: body.identitySource,
    ...(body.checklistId === undefined ? {} : { checklistId: body.checklistId }),
    ...(body.cardPresentation === undefined
      ? {}
      : {
          cardPresentation:
            body.cardPresentation.kind === 'RAW'
              ? { kind: 'RAW', condition: body.cardPresentation.condition }
              : {
                  kind: 'GRADED',
                  grader: body.cardPresentation.grader,
                  grade: body.cardPresentation.grade,
                  ...(body.cardPresentation.certificationNumber === undefined
                    ? {}
                    : { certificationNumber: body.cardPresentation.certificationNumber }),
                },
        }),
    ...(body.itemSpecifics === undefined
      ? {}
      : {
          itemSpecifics: Object.fromEntries(
            Object.entries(body.itemSpecifics).map(([name, values]) => {
              if (!Array.isArray(values) || values.some((v) => typeof v !== 'string'))
                throw new Error('Invalid CCG specifics.');
              return [name, [...values]];
            }),
          ),
        }),
    itemSpecificsCategory: 'CCG_SINGLES' as const,
    ccgReview: {
      schemaVersion: 1 as const,
      policyReviewIdentity: review.policyReviewIdentity,
      specifics,
    },
  };
}

function ccgIdentity(identity: CardIdentity): CardIdentity {
  const keys = [
    'category',
    'set',
    'number',
    'name',
    'year',
    'brand',
    'subset',
    'attributes',
    'rookie',
    'autograph',
    'memorabilia',
    'serialNumbered',
    'team',
    'language',
    'variation',
    'serialTo',
    'serialNumber',
  ] as const;
  return Object.fromEntries(
    keys.filter((key) => identity[key] !== undefined).map((key) => [key, identity[key]]),
  ) as unknown as CardIdentity;
}
